import { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { UserProvider, useUsers } from "./context/UserContext";
import { EventsProvider } from "./context/EventsContext";
import { AvailabilityProvider } from "./context/AvailabilityContext";
import { PeriodProvider } from "./context/PeriodContext";
import { WerklijstenProvider } from "./context/WerklijstenContext";
import { AppProvider } from "./context/AppContext";
import { WebSocketProvider } from "./context/WebSocketContext";
import { TranslationProvider } from "./context/TranslationContext";
import { HolidayProvider } from "./context/HolidayContext";
import { TooltipProvider } from "./context/TooltipContext";
import { useGlobalTitleInterceptor } from "./hooks/useGlobalTitleInterceptor";
import { VersionDisplay } from "./components/common/VersionDisplay";
import { Toaster } from "react-hot-toast";

// Lazy load route components for code splitting
const Calendar = lazy(() => import("./components/Calendar").then(m => ({ default: m.Calendar })));
const Login = lazy(() => import("./components/Login").then(m => ({ default: m.Login })));

// Component to initialize global title interception
function GlobalTooltipHandler() {
  useGlobalTitleInterceptor();
  return null;
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  const { isLoadingUsers } = useUsers();

  if (isLoadingUsers) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" />;
  }

  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    }>
      {children}
    </Suspense>
  );
}

function App() {
  return (
    <TranslationProvider>
      <TooltipProvider>
        <Router
          future={{
            v7_relativeSplatPath: true,
            v7_startTransition: true,
          }}
        >
          <AuthProvider>
          <UserProvider>
            <EventsProvider>
              <AvailabilityProvider>
                <PeriodProvider>
                  <WerklijstenProvider>
                    <WebSocketProvider>
                      <HolidayProvider>
                        <AppProvider>
                        <GlobalTooltipHandler />
                        <div className="min-h-screen bg-zinc-50" data-tsx-id="app-root">
                  <Suspense fallback={
                    <div className="min-h-screen flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  }>
                    <Routes>
                      <Route path="/login" element={<Login />} />
                      <Route
                        path="/"
                        element={
                          <PrivateRoute>
                            <Calendar />
                          </PrivateRoute>
                        }
                      />
                    </Routes>
                  </Suspense>
                  <VersionDisplay />
                  <Toaster
                    position="top-right"
                    toastOptions={{
                      duration: 3000,
                      style: {
                        background: "#333",
                        color: "#fff",
                      },
                      success: {
                        style: {
                          background: "#059669",
                        },
                      },
                      error: {
                        style: {
                          background: "#DC2626",
                        },
                        duration: 4000,
                      },
                    }}
                  />
                        </div>
                        </AppProvider>
                      </HolidayProvider>
                    </WebSocketProvider>
                  </WerklijstenProvider>
                </PeriodProvider>
              </AvailabilityProvider>
            </EventsProvider>
          </UserProvider>
          </AuthProvider>
        </Router>
      </TooltipProvider>
    </TranslationProvider>
  );
}

export default App;